import LocalizedLink from "@/components/LocalizedLink";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { COMMUNITY_LINKS, EMAIL_HREF } from "@/data/community";
import { useLanguage } from "@/i18n/LanguageContext";

/* -------------------------------------------------------------------- */
/*  Bilingual copy                                                       */
/* -------------------------------------------------------------------- */
const FT = {
  ar: {
    // Column 1
    brand: "مجتمع الذكاء الاصطناعي السوري",
    about:
      "مبادرة تطوعية غير ربحية تهدف إلى نشر الوعي العملي بالذكاء الاصطناعي في سوريا، عبر أدوات مجانية، محتوى عربي مبسّط، وورشات تعليمية تطبيقية للطلاب والباحثين والمجتمع.",

    // Column 2 — Links
    linksTitle: "روابط",
    aboutLink: "عن المبادرة",
    toolsLink: "دليل الأدوات",
    workshopsLink: "الورشات",
    articlesLink: "المقالات",
    impactLink: "الأثر",
    teamLink: "الفريق",

    // Column 3 — Contact
    contactTitle: "تواصل",
    contactPartnerships: "للشراكات",
    contactHostWorkshop: "لاستضافة ورشة",
    contactGeneral: "استفسارات عامة",
    contactPage: "صفحة التواصل",

    // Column 4 — Support
    supportTitle: "الدعم",
    supportCenter: "مركز الدعم",
    supportRequest: "طلب دعم",
    supportCommunity: "مجتمع SAS",
    supportContactMethods: "طرق التواصل",

    // Column 5 — Community (new)
    communityTitle: "المجتمع",
    communityWhatsAppGroup: "مجموعة واتساب",
    communityWhatsAppChannel: "قناة واتساب",
    communityEmail: "راسلنا",

    // Bottom row
    rights: "جميع الحقوق محفوظة.",
    bwe: "مدعومة من Emergent",
    privacy: "سياسة الخصوصية",
    terms: "شروط الاستخدام",
  },
  en: {
    // Column 1
    brand: "Syrian AI Society",
    about:
      "A non-profit volunteer initiative advancing practical AI awareness in Syria through free tools, simplified Arabic content, and hands-on educational workshops for students, researchers, and the wider community.",

    // Column 2 — Links
    linksTitle: "Links",
    aboutLink: "About the initiative",
    toolsLink: "Tools directory",
    workshopsLink: "Workshops",
    articlesLink: "Articles",
    impactLink: "Impact",
    teamLink: "Team",

    // Column 3 — Contact
    contactTitle: "Contact",
    contactPartnerships: "For partnerships",
    contactHostWorkshop: "Host a workshop",
    contactGeneral: "General inquiries",
    contactPage: "Contact page",

    // Column 4 — Support
    supportTitle: "Support",
    supportCenter: "Support Center",
    supportRequest: "Request Support",
    supportCommunity: "SAS Community",
    supportContactMethods: "Contact Methods",

    // Column 5 — Community (new)
    communityTitle: "Community",
    communityWhatsAppGroup: "WhatsApp Group",
    communityWhatsAppChannel: "WhatsApp Channel",
    communityEmail: "Email us",

    // Bottom row
    rights: "All rights reserved.",
    bwe: "Powered by Emergent",
    privacy: "Privacy Policy",
    terms: "Terms of Use",
  },
};

/* -------------------------------------------------------------------- */
/*  Helpers                                                              */
/* -------------------------------------------------------------------- */

/** A small, tap-friendly footer link. The `lang`-aware prefix is
 *  handled by <LocalizedLink>, so callers always pass the canonical
 *  English path (e.g. `/tools`, `/contact/partnership`). */
function FooterLink({ to, children, testId }) {
  return (
    <li>
      <LocalizedLink
        to={to}
        className="
          inline-block py-1 text-[14px] leading-[1.7]
          text-[color:var(--sas-ink-2)]
          hover:text-[color:var(--sas-accent)]
          transition-colors
        "
        data-testid={testId}
      >
        {children}
      </LocalizedLink>
    </li>
  );
}

/** External / mailto variant — same visual styling as `FooterLink`,
 *  but opens the URL in a new tab (or the user's mail client) so the
 *  Community column items behave correctly. */
function FooterExternalLink({ href, external = true, children, testId }) {
  return (
    <li>
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className="
          inline-block py-1 text-[14px] leading-[1.7]
          text-[color:var(--sas-ink-2)]
          hover:text-[color:var(--sas-accent)]
          transition-colors
        "
        data-testid={testId}
      >
        {children}
      </a>
    </li>
  );
}

function FooterColumn({ title, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="sas-eyebrow mb-4">{title}</div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Footer                                                               */
/* -------------------------------------------------------------------- */
export default function Footer() {
  const { lang } = useLanguage();
  const t = FT[lang];
  const year = new Date().getFullYear();

  return (
    <footer
      className="border-t border-[color:var(--sas-line)] bg-white"
      data-testid="footer"
    >
      <div className="sas-container py-12 md:py-14">
        {/*
          Body grid.
          - Mobile  : 1 col (description first, then each section stacks).
          - sm      : 2 cols (paired columns share rows).
          - lg      : description + 4 link columns side-by-side.
          The description column stays widest on lg so the paragraph
          isn't forced into too narrow a measure.
        */}
        <div
          className="
            grid gap-10
            sm:grid-cols-2
            lg:grid-cols-[1.4fr_1fr_1fr_1fr_1fr]
          "
          data-testid="footer-grid"
        >
          {/* Column 1 — brand description */}
          <div className="sm:col-span-2 lg:col-span-1" data-testid="footer-brand">
            <div className="flex items-center gap-3 mb-4">
              <img src="/sas-logo.svg" alt="SAS logo" className="h-8 w-8" />
              <span className="font-semibold tracking-tight">{t.brand}</span>
            </div>
            <p className="sas-body text-sm leading-[1.85] max-w-md">
              {t.about}
            </p>
          </div>

          {/* Column 2 — Links */}
          <FooterColumn title={t.linksTitle} testId="footer-col-links">
            <FooterLink to="/about" testId="footer-link-about">
              {t.aboutLink}
            </FooterLink>
            <FooterLink to="/tools" testId="footer-link-tools">
              {t.toolsLink}
            </FooterLink>
            <FooterLink to="/workshops" testId="footer-link-workshops">
              {t.workshopsLink}
            </FooterLink>
            <FooterLink to="/articles" testId="footer-link-articles">
              {t.articlesLink}
            </FooterLink>
            <FooterLink to="/impact" testId="footer-link-impact">
              {t.impactLink}
            </FooterLink>
            <FooterLink to="/team" testId="footer-link-team">
              {t.teamLink}
            </FooterLink>
          </FooterColumn>

          {/* Column 3 — Contact (every item is a real link) */}
          <FooterColumn title={t.contactTitle} testId="footer-col-contact">
            <FooterLink
              to="/contact/partnership"
              testId="footer-link-contact-partnership"
            >
              {t.contactPartnerships}
            </FooterLink>
            <FooterLink
              to="/contact/host-workshop"
              testId="footer-link-contact-host"
            >
              {t.contactHostWorkshop}
            </FooterLink>
            <FooterLink
              to="/contact/general"
              testId="footer-link-contact-general"
            >
              {t.contactGeneral}
            </FooterLink>
            <FooterLink to="/contact" testId="footer-link-contact-page">
              {t.contactPage}
            </FooterLink>
          </FooterColumn>

          {/* Column 4 — Support */}
          <FooterColumn title={t.supportTitle} testId="footer-col-support">
            <FooterLink to="/support" testId="footer-link-support-center">
              {t.supportCenter}
            </FooterLink>
            <FooterLink
              to="/support/request"
              testId="footer-link-support-request"
            >
              {t.supportRequest}
            </FooterLink>
            <FooterLink to="/community" testId="footer-link-support-community">
              {t.supportCommunity}
            </FooterLink>
            <FooterLink to="/contact" testId="footer-link-support-contact">
              {t.supportContactMethods}
            </FooterLink>
          </FooterColumn>

          {/* Column 5 — Community (external channels) */}
          <FooterColumn title={t.communityTitle} testId="footer-col-community">
            <FooterExternalLink
              href={COMMUNITY_LINKS.whatsappGroup}
              testId="footer-link-whatsapp-group"
            >
              {t.communityWhatsAppGroup}
            </FooterExternalLink>
            <FooterExternalLink
              href={COMMUNITY_LINKS.whatsappChannel}
              testId="footer-link-whatsapp-channel"
            >
              {t.communityWhatsAppChannel}
            </FooterExternalLink>
            <FooterExternalLink
              href={EMAIL_HREF}
              external={false}
              testId="footer-link-community-email"
            >
              {t.communityEmail}
            </FooterExternalLink>
          </FooterColumn>
        </div>

        <div className="sas-divider mt-12 mb-6" />

        {/*
          Bottom utility row.
          - Desktop : a single horizontal line.
          - Mobile  : three vertical groups so nothing gets cramped.
        */}
        <div
          className="
            flex flex-col gap-5
            md:flex-row md:items-center md:justify-between md:gap-4
            text-xs text-[color:var(--sas-ink-2)]
          "
        >
          <span data-testid="footer-copyright">
            © {year} {t.brand} — {t.rights}
          </span>

          <nav
            className="
              flex flex-wrap items-center
              gap-x-4 gap-y-3
              md:gap-x-3
            "
            aria-label={lang === "ar" ? "روابط الذيل" : "Footer utility links"}
          >
            <LocalizedLink
              to="/privacy"
              className="hover:text-[color:var(--sas-accent)] transition-colors"
              data-testid="footer-privacy-link"
            >
              {t.privacy}
            </LocalizedLink>
            <span className="opacity-30" aria-hidden>·</span>
            <LocalizedLink
              to="/terms"
              className="hover:text-[color:var(--sas-accent)] transition-colors"
              data-testid="footer-terms-link"
            >
              {t.terms}
            </LocalizedLink>
            <span className="opacity-30" aria-hidden>·</span>
            <LanguageSwitcher
              variant="footer-pair"
              testId="lang-switcher-footer"
            />
            <span className="opacity-30" aria-hidden>·</span>
            <LocalizedLink
              to="/built-with-emergent"
              className="hover:text-[color:var(--sas-accent)] transition-colors"
              data-testid="footer-bwe-link"
            >
              {t.bwe}
            </LocalizedLink>
          </nav>

          <span dir="ltr" className="font-medium tracking-wide">
            Syrian AI Society · SAS
          </span>
        </div>
      </div>
    </footer>
  );
}
